import { useEffect, useState, useRef, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Download, FileText, Loader2, Send } from 'lucide-react';
import { addComment, getAttachmentDownloadUrl, getTicketDetail, type TicketAttachment, type TicketComment, type TicketDetail as TicketDetailType } from '@/api/support';
import { toUserMessage } from '@/lib/errors';
import { Button } from '@/components/assist/ui/button';
import { Input } from '@/components/assist/ui/input';
import { Card } from '@/components/assist/ui/card';
import { Skeleton } from '@/components/assist/ui/skeleton';
import { useSupportDrawer } from '@/contexts/SupportDrawerContext';

const STATUS_COLORS: Record<string, string> = {
  'Open': 'bg-[hsl(var(--status-healthy)/0.15)] text-status-healthy',
  'Waiting for support': 'bg-[hsl(var(--status-attention)/0.15)] text-status-attention',
  'In Progress': 'bg-[hsl(var(--primary)/0.15)] text-primary',
  'Resolved': 'bg-[hsl(var(--status-thriving)/0.15)] text-status-thriving',
  'Closed': 'bg-muted text-muted-foreground',
};

function getStatusColor(status: string) {
  return STATUS_COLORS[status] || 'bg-muted text-muted-foreground';
}

function DetailSkeleton() {
  return (
    <div className="space-y-4 flex-1">
      <div className="p-4 rounded-lg border border-border space-y-3">
        <div className="flex items-start justify-between gap-3">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-5 w-20 rounded-full" />
        </div>
        <Skeleton className="h-3 w-1/4" />
        <Skeleton className="h-16 w-full" />
      </div>
      <div className="space-y-3 pt-2">
        <div className="flex justify-start"><Skeleton className="h-16 w-3/4 rounded-lg" /></div>
        <div className="flex justify-end"><Skeleton className="h-12 w-2/3 rounded-lg" /></div>
      </div>
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function AttachmentItem({ attachment, ticketKey }: { attachment: TicketAttachment; ticketKey: string }) {
  const downloadUrl = attachment.content_url
    ? getAttachmentDownloadUrl(ticketKey, attachment.content_url)
    : null;

  return (
    <div className="flex items-center gap-2 text-sm bg-muted rounded-lg px-3 py-2">
      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="truncate flex-1">{attachment.filename}</span>
      <span className="text-xs text-muted-foreground shrink-0">{formatFileSize(attachment.size)}</span>
      {downloadUrl && (
        <a
          href={downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
        >
          <Download className="h-4 w-4" />
        </a>
      )}
    </div>
  );
}

function CommentBubble({ comment, isUser }: { comment: TicketComment; isUser: boolean }) {
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${
        isUser
          ? 'bg-primary text-primary-foreground rounded-br-md'
          : 'bg-muted rounded-bl-md'
      }`}>
        <p className="text-xs font-medium mb-1 opacity-70">{comment.author_name}</p>
        <p className="whitespace-pre-wrap leading-relaxed">{comment.body}</p>
        <p className="text-xs opacity-50 mt-1.5">
          {new Date(comment.created_at).toLocaleString()}
        </p>
      </div>
    </div>
  );
}

export function TicketDetail() {
  const { t } = useTranslation('assist');
  const { selectedTicketKey, setView } = useSupportDrawer();
  const [ticket, setTicket] = useState<TicketDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const commentsEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectedTicketKey) return;
    setLoading(true);
    setError(null);
    getTicketDetail(selectedTicketKey)
      .then(setTicket)
      .catch((e) => setError(toUserMessage(e, t, { context: 'loadTicketDetail' })))
      .finally(() => setLoading(false));
  }, [selectedTicketKey, t]);

  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ticket?.comments.length]);

  const handleReply = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedTicketKey || !replyText.trim()) return;
    setSending(true);
    try {
      const comment = await addComment(selectedTicketKey, replyText);
      setTicket((prev) =>
        prev ? { ...prev, comments: [...prev.comments, comment] } : prev
      );
      setReplyText('');
    } catch (e) {
      setError(toUserMessage(e, t, { context: 'sendTicketReply' }));
    } finally {
      setSending(false);
    }
  };

  const isUserComment = (comment: TicketComment) => {
    return !comment.author_email;
  };

  return (
    <div className="flex flex-col h-full">
      <button
        onClick={() => setView('list')}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('support.detail.back', 'Back to tickets')}
      </button>

      {loading && <DetailSkeleton />}

      {error && <p className="text-sm text-destructive py-4 text-center">{error}</p>}

      {!loading && ticket && (
        <>
          <Card className="shadow-card mb-4 shrink-0">
            <div className="p-4">
              <div className="flex items-start justify-between gap-3 mb-2">
                <h4 className="text-base font-semibold leading-snug">{ticket.summary}</h4>
                <span className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap font-medium shrink-0 ${getStatusColor(ticket.status)}`}>
                  {ticket.status}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">{ticket.key}</p>
              {ticket.description && (
                <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">{ticket.description}</p>
              )}
            </div>
          </Card>

          {ticket.attachments.length > 0 && (
            <div className="space-y-1.5 mb-4 shrink-0">
              <p className="text-xs font-medium text-muted-foreground">
                {t('support.detail.attachments', 'Attachments')}
              </p>
              {ticket.attachments.map((att, i) => (
                <AttachmentItem key={i} attachment={att} ticketKey={ticket.key} />
              ))}
            </div>
          )}

          <div className="flex-1 overflow-y-auto space-y-3 pr-1 min-h-0">
            {ticket.comments.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">
                {t('support.detail.noComments', 'No comments yet')}
              </p>
            )}
            {ticket.comments.map((comment) => (
              <CommentBubble key={comment.id} comment={comment} isUser={isUserComment(comment)} />
            ))}
            <div ref={commentsEndRef} />
          </div>

          <form onSubmit={handleReply} className="flex gap-2 pt-4 mt-auto border-t border-border shrink-0">
            <Input
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder={t('support.detail.replyPlaceholder', 'Type a reply...')}
              className="flex-1"
            />
            <Button type="submit" size="icon" disabled={sending || !replyText.trim()}>
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </form>
        </>
      )}
    </div>
  );
}
